import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import JsBarcode from "jsbarcode";
import { uiState } from "../../../state/uiState.js";
import { showDialogOverlay, hideDialogOverlay } from "../../../utils/dialogOverlay.js";
import { canUseBarcodeCart } from "../barcodeCartPermissions.js";

const BARCODE_TEMPLATE_95 = {
    pageWidthMm: 210,
    pageHeightMm: 297,
    columns: 5,
    rows: 19,
    capacity: 95,
    labelWidthMm: 30,
    labelHeightMm: 12,
    marginLeftMm: 18,
    marginTopMm: 3,
    columnGapMm: 6,
    rowGapMm: 3.5,
    barcodeWidthMm: 28.4,
    barcodeHeightMm: 8.9,
    barcodeTopOffsetMm: 0.8,
    textFontSizePt: 4.6,
    textBottomOffsetMm: 0.5,
    showValueText: true,
};

let didBindBarcodeCartDialog = false;
let sortableInstance = null;

function ensureBarcodeCartDialog() {
    if (document.getElementById("barcodeCartDialog")) return;
    document.body.insertAdjacentHTML("beforeend", `
<dialog id="barcodeCartDialog">
    <div class="barcode-cart-dialog">
        <div class="barcode-cart-header">
            <div class="barcode-cart-title">
                <span class="material-icons">shopping_basket</span>
                <span>Barkod Sepeti</span>
            </div>
            <button type="button" class="barcode-cart-close" title="Kapat">
                <span class="material-icons">close</span>
            </button>
        </div>
        <div class="barcode-cart-body">
            <div class="barcode-cart-info-box">
                <div class="barcode-cart-info-primary">95 etiket. 0 ürün.</div>
                <div class="barcode-cart-info-secondary">Sepet boş.</div>
            </div>

            <div class="barcode-cart-settings">
                <div class="column-container">
                    <label for="barcode_cart_start">Başlangıç Etiketi</label>
                    <input type="text" id="barcode_cart_start" inputmode="numeric" autocomplete="off" value="1" maxlength="2" />
                </div>
                <div class="barcode-cart-settings-note">
                    Basılacak etkiketlerin etiket kağıdında başlayacağı hücreyi verir.
                </div>
            </div>

            <div class="barcode-cart-list"></div>
        </div>
        <div class="barcode-cart-footer">
            <span class="barcode-cart-footer-count">0 ürün</span>
            <div class="barcode-cart-footer-actions">
                <button type="button" class="btn-header-action barcode-cart-clear">
                    <span class="material-icons">delete_outline</span>
                    Sepeti Temizle
                </button>
                <button type="button" class="btn-header-action btn-header-action--primary barcode-cart-download">
                    <span class="material-icons">picture_as_pdf</span>
                    PDF İndir
                </button>
            </div>
        </div>
    </div>
</dialog>`);
}

function showToast(text, background = "#42525e") {
    Toastify({
        text,
        duration: 2500,
        gravity: "top",
        position: "right",
        style: {
            background,
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: "500",
        },
    }).showToast();
}

function mmToPt(mm) {
    return (mm * 72) / 25.4;
}

function clampStartPosition(rawValue) {
    const digits = String(rawValue ?? "").replace(/\D+/g, "");
    if (!digits) return "";
    return String(Math.max(1, Math.min(BARCODE_TEMPLATE_95.capacity, Number(digits))));
}

function keepDigitsOnly(rawValue) {
    return String(rawValue ?? "").replace(/\D+/g, "");
}

function getStartInput() {
    return document.getElementById("barcode_cart_start");
}

function getRawStartPosition() {
    const input = getStartInput();
    return keepDigitsOnly(input?.value ?? "");
}

function getNormalizedStartPosition() {
    const input = getStartInput();
    if (!input) return 1;
    const sanitized = clampStartPosition(input.value) || "1";
    input.value = sanitized;
    return Number(sanitized);
}

function getCartDialog() {
    return document.getElementById("barcodeCartDialog");
}

function getCartSummary(itemCount = uiState.demirbas.cart.length, start = null) {
    const resolvedStart = start == null ? getNormalizedStartPosition() : start;
    const blankCount = Math.max(0, resolvedStart - 1);
    const filledSlotCount = blankCount + itemCount;
    const totalPages = filledSlotCount > 0 ? Math.ceil(filledSlotCount / BARCODE_TEMPLATE_95.capacity) : 0;

    return {
        itemCount,
        start: resolvedStart,
        blankCount,
        totalPages,
    };
}

function escapeHtml(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function updateBarcodeCartInfo() {
    const dialog = getCartDialog();
    if (!dialog) return;

    const infoPrimary = dialog.querySelector(".barcode-cart-info-primary");
    const infoSecondary = dialog.querySelector(".barcode-cart-info-secondary");
    const footerCount = dialog.querySelector(".barcode-cart-footer-count");
    const clearBtn = dialog.querySelector(".barcode-cart-clear");
    const downloadBtn = dialog.querySelector(".barcode-cart-download");
    const rawStart = getRawStartPosition();
    const resolvedStart = rawStart ? Number(clampStartPosition(rawStart)) : null;
    const { itemCount, start, blankCount, totalPages } = getCartSummary(
        uiState.demirbas.cart.length,
        resolvedStart ?? 1,
    );

    if (infoPrimary) {
        infoPrimary.textContent = `95 etiket. ${itemCount} ürün. Başlangıç: ${resolvedStart ?? "—"}.`;
    }

    if (infoSecondary) {
        if (!rawStart) infoSecondary.textContent = "Başlangıç etiketi girin.";
        else if (!itemCount) infoSecondary.textContent = "Sepet boş.";
        else if (totalPages <= 1) infoSecondary.textContent = `${blankCount} etiket boş geçecek. Tek sayfa yeterli.`;
        else infoSecondary.textContent = `${blankCount} etiket boş geçecek. ${totalPages} sayfa oluşacak.`;
    }

    if (footerCount) footerCount.textContent = `${itemCount} ürün`;
    if (clearBtn) clearBtn.disabled = itemCount === 0;
    if (downloadBtn) downloadBtn.disabled = itemCount === 0;
}

function updateBarcodeCartBadge() {
    const count = uiState.demirbas.cart.length;
    document.querySelectorAll(".barcode-cart-badge").forEach((badge) => {
        badge.textContent = String(count);
        badge.hidden = count === 0;
    });
}

function renderBarcodeCartList() {
    const dialog = getCartDialog();
    const list = dialog?.querySelector(".barcode-cart-list");
    if (!dialog || !list) return;

    if (sortableInstance) {
        sortableInstance.destroy();
        sortableInstance = null;
    }

    if (!uiState.demirbas.cart.length) {
        list.innerHTML = `
            <div class="barcode-cart-empty">
                <span class="material-icons">inventory_2</span>
                <span>Sepet boş.</span>
            </div>`;
        updateBarcodeCartInfo();
        return;
    }

    list.innerHTML = uiState.demirbas.cart
        .map((item) => `
            <div class="barcode-cart-item" data-id="${escapeHtml(item.demirbas_id)}">
                <button type="button" class="barcode-cart-handle" title="Sırayı değiştir">
                    <span class="material-icons">drag_indicator</span>
                </button>
                <div class="barcode-cart-item-main">
                    <div class="barcode-cart-item-id">${escapeHtml(item.demirbas_id)}</div>
                    <div class="barcode-cart-item-name">${escapeHtml(item.shortLabel)}</div>
                </div>
                <div class="barcode-cart-item-barcode">${escapeHtml(item.barkod)}</div>
                <button type="button" class="barcode-cart-remove" data-id="${escapeHtml(item.demirbas_id)}" title="Çıkar">
                    <span class="material-icons">close</span>
                </button>
            </div>`)
        .join("");

    if (window.Sortable && uiState.demirbas.cart.length > 1) {
        sortableInstance = new window.Sortable(list, {
            animation: 150,
            handle: ".barcode-cart-handle",
            ghostClass: "barcode-cart-item--ghost",
            chosenClass: "barcode-cart-item--chosen",
            onEnd: ({ oldIndex, newIndex }) => {
                if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
                const moved = uiState.demirbas.cart.splice(oldIndex, 1)[0];
                uiState.demirbas.cart.splice(newIndex, 0, moved);
                renderBarcodeCartList();
                updateBarcodeCartBadge();
            },
        });
    }

    updateBarcodeCartInfo();
}

function renderBarcodeCartUi() {
    updateBarcodeCartBadge();
    renderBarcodeCartList();
}

function buildBarcodeImageDataUrl(value) {
    const canvas = document.createElement("canvas");
    canvas.width = 720;
    canvas.height = 200;

    JsBarcode(canvas, String(value), {
        format: "CODE128",
        width: 2.4,
        height: 92,
        displayValue: false,
        margin: 0,
        background: "#ffffff",
        lineColor: "#111827",
    });

    return canvas.toDataURL("image/png");
}

async function downloadBarcodeCartPdf() {
    const dialog = getCartDialog();
    const items = uiState.demirbas.cart;
    if (!dialog || !items.length) return;

    if (!window.PDFLib) {
        showToast("PDF kütüphanesi yüklenemedi.", "#ee4447");
        return;
    }

    const start = getNormalizedStartPosition();
    const { PDFDocument, StandardFonts, rgb } = window.PDFLib;

    showDialogOverlay(dialog, "PDF hazırlanıyor...");
    await new Promise((r) => setTimeout(r, 350));

    try {
        const pdfDoc = await PDFDocument.create();
        const font = BARCODE_TEMPLATE_95.showValueText
            ? await pdfDoc.embedFont(StandardFonts.Helvetica)
            : null;
        const pageWidthPt = mmToPt(BARCODE_TEMPLATE_95.pageWidthMm);
        const pageHeightPt = mmToPt(BARCODE_TEMPLATE_95.pageHeightMm);
        const pages = [];
        const barcodeCache = new Map();

        const getPage = (pageIndex) => {
            if (!pages[pageIndex]) pages[pageIndex] = pdfDoc.addPage([pageWidthPt, pageHeightPt]);
            return pages[pageIndex];
        };

        for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
            const item = items[itemIndex];
            const absoluteSlotIndex = (start - 1) + itemIndex;
            const pageIndex = Math.floor(absoluteSlotIndex / BARCODE_TEMPLATE_95.capacity);
            const slotIndexOnPage = absoluteSlotIndex % BARCODE_TEMPLATE_95.capacity;
            const rowIndex = Math.floor(slotIndexOnPage / BARCODE_TEMPLATE_95.columns);
            const colIndex = slotIndexOnPage % BARCODE_TEMPLATE_95.columns;
            const labelLeftMm = BARCODE_TEMPLATE_95.marginLeftMm + (colIndex * (BARCODE_TEMPLATE_95.labelWidthMm + BARCODE_TEMPLATE_95.columnGapMm));
            const labelTopMm = BARCODE_TEMPLATE_95.marginTopMm + (rowIndex * (BARCODE_TEMPLATE_95.labelHeightMm + BARCODE_TEMPLATE_95.rowGapMm));
            const page = getPage(pageIndex);

            let embeddedBarcode = barcodeCache.get(item.barkod);
            if (!embeddedBarcode) {
                embeddedBarcode = await pdfDoc.embedPng(buildBarcodeImageDataUrl(item.barkod));
                barcodeCache.set(item.barkod, embeddedBarcode);
            }

            const barcodeXPt = mmToPt(labelLeftMm + ((BARCODE_TEMPLATE_95.labelWidthMm - BARCODE_TEMPLATE_95.barcodeWidthMm) / 2));
            const barcodeYPt = pageHeightPt - mmToPt(labelTopMm + BARCODE_TEMPLATE_95.barcodeTopOffsetMm + BARCODE_TEMPLATE_95.barcodeHeightMm);
            const barcodeWidthPt = mmToPt(BARCODE_TEMPLATE_95.barcodeWidthMm);
            const barcodeHeightPt = mmToPt(BARCODE_TEMPLATE_95.barcodeHeightMm);

            page.drawImage(embeddedBarcode, {
                x: barcodeXPt,
                y: barcodeYPt,
                width: barcodeWidthPt,
                height: barcodeHeightPt,
            });

            if (BARCODE_TEMPLATE_95.showValueText && font) {
                const text = String(item.barkod);
                const textWidth = font.widthOfTextAtSize(text, BARCODE_TEMPLATE_95.textFontSizePt);
                const textX = mmToPt(labelLeftMm) + ((mmToPt(BARCODE_TEMPLATE_95.labelWidthMm) - textWidth) / 2);
                const textY = pageHeightPt - mmToPt(labelTopMm + BARCODE_TEMPLATE_95.labelHeightMm - BARCODE_TEMPLATE_95.textBottomOffsetMm) - BARCODE_TEMPLATE_95.textFontSizePt;

                page.drawText(text, {
                    x: textX,
                    y: textY,
                    size: BARCODE_TEMPLATE_95.textFontSizePt,
                    font,
                    color: rgb(0.12, 0.16, 0.22),
                });
            }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });
        const fileName = `barkodlar-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.pdf`;
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    } catch (err) {
        console.error("❌ Barkod PDF üretilemedi:", err);
        showToast("PDF hazırlanırken hata oluştu.", "#ee4447");
    } finally {
        hideDialogOverlay();
    }
}

export function addToBarcodeCart(item) {
    if (!canUseBarcodeCart(uiState.currentUser)) return;
    if (!item?.demirbas_id || !item?.barkod) {
        showToast("Barkod bilgisi eksik.", "#ee4447");
        return;
    }

    const exists = uiState.demirbas.cart.some((cartItem) => cartItem.demirbas_id === item.demirbas_id);
    if (exists) {
        showToast("Ürün sepette var.");
        return;
    }

    uiState.demirbas.cart.push({
        demirbas_id: String(item.demirbas_id),
        barkod: String(item.barkod),
        shortLabel: String(item.shortLabel ?? "").trim(),
    });

    renderBarcodeCartUi();
}

export function updateBarcodeCartUi() {
    renderBarcodeCartUi();
}

export function closeBarcodeCartDialog() {
    const dialog = getCartDialog();
    if (dialog?.open) dialog.close();
}

export function resetDemirbasBarcodeCart() {
    uiState.demirbas.cart = [];
    const startInput = getStartInput();
    if (startInput) startInput.value = "1";
    closeBarcodeCartDialog();
    renderBarcodeCartUi();
}

export function initBarcodeCartDialog() {
    ensureBarcodeCartDialog();
    renderBarcodeCartUi();

    const dialog = getCartDialog();
    if (!dialog || didBindBarcodeCartDialog) return;
    didBindBarcodeCartDialog = true;

    document.body.addEventListener("click", (event) => {
        if (event.target.closest(".barcode-cart-open-btn")) {
            if (!canUseBarcodeCart(uiState.currentUser)) return;
            renderBarcodeCartUi();
            if (!dialog.open) dialog.showModal();
        }

        if (event.target.closest(".barcode-cart-close")) {
            dialog.close();
        }

        const removeBtn = event.target.closest(".barcode-cart-remove");
        if (removeBtn) {
            uiState.demirbas.cart = uiState.demirbas.cart.filter((item) => item.demirbas_id !== removeBtn.dataset.id);
            renderBarcodeCartUi();
        }

        if (event.target.closest(".barcode-cart-clear")) {
            uiState.demirbas.cart = [];
            const startInput = getStartInput();
            if (startInput) startInput.value = "1";
            renderBarcodeCartUi();
        }

        if (event.target.closest(".barcode-cart-download")) {
            downloadBarcodeCartPdf();
        }
    });

    getStartInput()?.addEventListener("input", (event) => {
        event.target.value = keepDigitsOnly(event.target.value);
        updateBarcodeCartInfo();
    });

    getStartInput()?.addEventListener("blur", (event) => {
        event.target.value = clampStartPosition(event.target.value) || "1";
        updateBarcodeCartInfo();
    });
}
